<template>
  <div
    class="chat-input-container"
    :class="{
      'chat-input-container--inline': inline,
      'chat-input-container--expanded': isExpanded,
      'chat-input-container--drag-over': isDraggingOver,
    }"
    @dragenter.prevent="onDragEnter"
    @dragover.prevent="onDragOver"
    @dragleave.prevent="onDragLeave"
    @drop.prevent="onDrop"
  >
    <div v-if="isDraggingOver" class="drop-hint">
      <i class="fas fa-file-upload"></i>
      Drop to attach — up to {{ maxFiles }} files,
      {{ formatFileSize(maxFileSize) }} each
    </div>

    <transition name="file-preview-slide">
      <div v-if="attachmentDrafts.length > 0" class="file-previews">
        <attachment-chip
          v-for="draft in attachmentDrafts"
          :key="draft.id"
          :entry="draft"
          @remove="removeAttachment(draft.id)"
          @retry="$emit('retry-attachment', draft.id)"
        />
      </div>
    </transition>

    <div v-if="notices.length" class="attach-notices" role="status">
      <i class="fas fa-exclamation-triangle attach-notices__icon"></i>
      <ul class="attach-notices__list">
        <li v-for="(notice, index) in notices" :key="index">
          <strong>{{ truncateFileName(notice.name, 32) }}</strong> was not
          attached — <span>{{ notice.reason }}</span>
        </li>
      </ul>
      <button class="attach-notices__dismiss" @click="dismissNotices">
        Dismiss
      </button>
    </div>

    <!-- Files settling, said to screen readers -->
    <div class="sr-only" aria-live="polite">{{ liveMessage }}</div>

    <div class="input-area">
      <div v-if="patientCapsuleLabel" class="patient-capsule">
        <i class="fas fa-user-circle"></i>
        <span
          >Client: <strong>{{ patientCapsuleLabel }}</strong></span
        >
        <button
          class="patient-capsule-close"
          @click="removeCapsule"
          :disabled="disabled"
          title="Exit client mode"
        >
          <i class="fas fa-times"></i>
        </button>
      </div>

      <div class="input-row">
        <textarea
          ref="inputField"
          v-model="inputText"
          class="chat-input"
          :placeholder="currentPlaceholder"
          :disabled="disabled"
          @keydown="handleKeyDown"
          @input="handleInput"
          @paste="onPaste"
          rows="1"
        ></textarea>
      </div>

      <div class="input-bottom-row">
        <!-- Hidden File Input -->
        <input
          ref="fileInput"
          type="file"
          multiple
          :accept="allowedTypes.join(',')"
          @change="handleFileSelect"
          style="display: none"
        />

        <div class="input-left-actions">
          <!-- Attachment Button -->
          <button
            class="input-button input-button--icon"
            :class="{ 'input-button--has-files': attachmentDrafts.length > 0 }"
            @click="handleAttachClick"
            :disabled="disabled || attachDisabled"
            v-tooltip="
              attachDisabled && attachTooltip ? attachTooltip : attachHint
            "
          >
            <i class="fas fa-paperclip"></i>
          </button>

          <button
            v-if="!isRecording"
            class="input-button input-button--icon"
            :class="{ 'input-button--transcribing': isTranscribing }"
            @click="startRecording"
            :disabled="disabled || isTranscribing"
            title="Voice input"
          >
            <i v-if="isTranscribing" class="fas fa-circle-notch fa-spin"></i>
            <i v-else class="fas fa-microphone"></i>
          </button>
          <button
            v-else
            class="input-button input-button--icon input-button--recording"
            @click="stopRecording"
            title="Stop recording"
          >
            <i class="fas fa-stop"></i>
          </button>

          <!-- Prompts Toggle -->
          <button
            v-if="showSuggestionsButton"
            class="input-button input-button--icon"
            :class="{ 'input-button--footer-active': suggestionsOpen }"
            @click="handleLightbulb"
            :disabled="disabled"
            title="Prompt templates"
          >
            <i class="fas fa-lightbulb"></i>
          </button>

          <!-- Web Access Switch: quiet when on (the default), marked when turned off -->
          <button
            v-if="showWebSearch"
            class="input-button input-button--icon"
            :class="{ 'input-button--web-off': !webSearchActive }"
            @click="toggleWebSearch"
            :disabled="disabled"
            v-tooltip="webAccessTooltip"
            aria-label="Web access"
            :aria-pressed="webSearchActive"
          >
            <i class="fas fa-globe"></i>
          </button>

          <!-- Patient Mode Toggle -->
          <button
            v-if="showPatientMode"
            class="input-button input-button--icon"
            :class="{
              'input-button--footer-active':
                patientModeActive && !canSwitchPatient,
              'input-button--switch': canSwitchPatient,
            }"
            @click="togglePatientMode"
            :disabled="disabled"
            :title="
              canSwitchPatient
                ? patientModeBtnLabel
                : patientModeActive
                ? 'Disable client mode'
                : 'Enable client mode'
            "
          >
            <i
              :class="
                canSwitchPatient ? 'fas fa-exchange-alt' : 'fas fa-user-md'
              "
            ></i>
          </button>
        </div>

        <div class="input-right-actions">
          <span v-if="attachmentDrafts.length" class="attach-counter"
            >{{ attachmentDrafts.length }} of {{ maxFiles }} files</span
          >
          <button
            class="input-button input-button--icon input-button--expand"
            @click="toggleExpand"
            :disabled="disabled"
            :title="isExpanded ? 'Collapse' : 'Expand for long text'"
          >
            <i
              :class="isExpanded ? 'fas fa-compress-alt' : 'fas fa-expand-alt'"
            ></i>
          </button>

          <button
            class="input-button input-button--send"
            :class="{ 'input-button--processing': isWaitingForResponse }"
            @click="handleSend"
            :disabled="disabled || !canSend"
            :title="isWaitingForResponse && canSend ? 'Add to queue' : 'Send'"
          >
            <!-- The pulse says Oliver is busy, but once there is something to
                 send the button has a job again: it queues. Showing a pulse then
                 hides a control that works. -->
            <span
              v-if="isWaitingForResponse && !canSend"
              class="pulse-indicator"
            ></span>
            <i v-else class="fas fa-paper-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AttachmentChip from "@/components/OliverBotV2/attachments/AttachmentChip.vue";
import { announcement } from "@/components/OliverBotV2/attachments/attachmentStatus.js";
import audioRecorder from "@/mixins/audioRecorder.js";
import {
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS,
} from "@/store/oliverChatBotV2.js";
import oliverClient from "@/lib/OliverClient";
import { firstRowEnd } from "@/components/OliverBotV2/utils/firstRowEnd.js";
import {
  formatFileSize,
  getFileIcon,
  truncateFileName,
} from "@/utils/fileHelpers.js";

export default {
  name: "ChatInput",
  components: { AttachmentChip },
  mixins: [audioRecorder],
  props: {
    placeholder: {
      type: String,
      default: "Ask anything",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    showSuggestionsButton: {
      type: Boolean,
      default: true,
    },
    showPatientMode: {
      type: Boolean,
      default: false,
    },
    patientModeActive: {
      type: Boolean,
      default: false,
    },
    patientModeBtnLabel: {
      type: String,
      default: "Client Mode",
    },
    canSwitchPatient: {
      type: Boolean,
      default: false,
    },
    patientCapsuleLabel: {
      type: String,
      default: null,
    },
    showWebSearch: {
      type: Boolean,
      default: true,
    },
    webSearchActive: {
      type: Boolean,
      default: true,
    },
    isWaitingForResponse: {
      type: Boolean,
      default: false,
    },
    queueFull: {
      type: Boolean,
      default: false,
    },
    isTranscribing: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
    suggestionsOpen: {
      type: Boolean,
      default: false,
    },
    attachmentDrafts: {
      type: Array,
      default: () => [],
    },
    // Files the server refused when asked to attach them.
    attachmentNotices: {
      type: Array,
      default: () => [],
    },
    // Files being created on the server, not yet chips: they count against the limit.
    attachmentsCreating: {
      type: Number,
      default: 0,
    },
    attachDisabled: {
      type: Boolean,
      default: false,
    },
    attachTooltip: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      inputText: "",
      isDraggingOver: false,
      dragDepth: 0,
      maxFileSize: MAX_ATTACHMENT_BYTES,
      maxFiles: MAX_ATTACHMENTS,
      // Files refused here, before any upload: shown under the composer, not in a dialog.
      localNotices: [],
      liveMessage: "",
      settled: {},
      allowedTypes: [
        // Plain text / markdown
        "text/plain",
        "text/markdown",
        ".txt",
        ".text",
        ".md",
        // Closed-caption transcripts (no registered MIME for .srt, so it's
        // matched by extension only)
        "text/vtt",
        ".vtt",
        ".srt",
        // PDF
        "application/pdf",
        ".pdf",
        // Images
        "image/png",
        "image/jpeg",
        "image/tiff",
        "image/webp",
        "image/bmp",
        "image/gif",
        ".png",
        ".jpg",
        ".jpeg",
        ".tiff",
        ".webp",
        ".bmp",
        ".gif",
        // Word
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".doc",
        ".docx",
        // Rich Text Format
        "application/rtf",
        "text/rtf",
        ".rtf",
        // HTML
        "text/html",
        ".html",
        ".htm",
        // Excel
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ".xls",
        ".xlsx",
        // PowerPoint
        "application/vnd.ms-powerpoint",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ".ppt",
        ".pptx",
        // OpenDocument
        "application/vnd.oasis.opendocument.text",
        "application/vnd.oasis.opendocument.spreadsheet",
        "application/vnd.oasis.opendocument.presentation",
        ".odt",
        ".ods",
        ".odp",
        // CSV
        "text/csv",
        ".csv",
        // JSON / XML (structured data, flattened to prose)
        "application/json",
        ".json",
        "application/xml",
        "text/xml",
        ".xml",
        // DICOM medical images. Browsers don't recognise .dcm, so the file
        // picker matches on the extension rather than the mime type.
        "application/dicom",
        ".dcm",
        ".dicom",
      ],
      isExpanded: false,
    };
  },
  computed: {
    attachHint() {
      return `Attach files — up to ${this.maxFiles}, ${this.formatFileSize(
        this.maxFileSize
      )} each`;
    },
    webAccessTooltip() {
      const clientLabel =
        this.$customerLabel?.({ type: "singular", capitalize: true }) ||
        "Client";
      return {
        content: this.webSearchActive
          ? `<b>Web access on</b><br>Oliver can search the web.<br>${clientLabel} details never sent.`
          : "<b>Web access off</b><br>CRM and files only.<br>Click to turn on.",
        html: true,
      };
    },
    // Files never hold Send back: Oliver waits for the ones still being read, and says so.
    canSend() {
      const hasText = this.inputText.trim().length > 0;
      return (hasText || this.attachmentDrafts.length > 0) && !this.queueFull;
    },
    notices() {
      return [...this.localNotices, ...this.attachmentNotices];
    },
    currentPlaceholder() {
      if (this.isTranscribing) {
        return "Transcribing audio...";
      }
      return this.placeholder;
    },
  },
  watch: {
    attachmentDrafts(drafts) {
      for (const draft of drafts) {
        // Announced when it changes here, not when it is first seen (a chat switched to).
        const seen = draft.id in this.settled;
        const line = announcement(draft);
        if (seen && line && this.settled[draft.id] !== draft.status) {
          this.liveMessage = line;
        }
        this.settled[draft.id] = draft.status;
      }
    },
  },
  async mounted() {
    try {
      const { data } = await oliverClient.getSupportedAttachmentTypes();
      if (data?.extensions?.length) {
        this.allowedTypes = [...data.extensions];
      }
    } catch (err) {
      // Keep the built-in list; an unreachable endpoint must not disable attaching.
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
      this.$emit("toggle-expand", this.isExpanded);
      this.$nextTick(() => {
        if (this.$refs.inputField) this.$refs.inputField.focus();
        if (this.isExpanded) {
          if (this.$refs.inputField) {
            this.$refs.inputField.style.height = "";
            this.$refs.inputField.style.overflowY = "";
          }
        } else {
          this.autoResize();
        }
      });
    },
    setInputValue(text, { cursorAtStart = false } = {}) {
      this.inputText = text;
      this.$nextTick(() => {
        this.autoResize();
        const field = this.$refs.inputField;
        if (!field) return;
        field.focus();
        const rowHeight = parseFloat(getComputedStyle(field).lineHeight);
        const hiddenHeight = field.scrollHeight - field.clientHeight;
        if (cursorAtStart && hiddenHeight > rowHeight) {
          const end = firstRowEnd(field, text);
          field.setSelectionRange(end, end);
          field.scrollTop = 0;
        }
      });
    },
    clearDraft() {
      this.inputText = "";
      this.$nextTick(() => {
        this.autoResize();
      });
    },
    handleInput() {
      this.autoResize();
    },
    autoResize() {
      if (this.isExpanded) return;
      const textarea = this.$refs.inputField;
      if (textarea) {
        textarea.style.height = "auto";

        const scrollHeight = textarea.scrollHeight;
        const maxHeight = 150;

        const newHeight = Math.min(scrollHeight, maxHeight);
        textarea.style.height = newHeight + "px";

        textarea.style.overflowY = scrollHeight > maxHeight ? "auto" : "hidden";
      }
    },
    handleKeyDown(event) {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        this.handleSend();
      }
    },
    handleSend() {
      if (!this.canSend || this.disabled) return;

      const message = this.inputText.trim();

      this.inputText = "";
      this.localNotices = [];
      this.$nextTick(() => {
        this.autoResize();
      });

      this.$emit("send", { text: message });
    },
    handleLightbulb() {
      this.$emit("toggle-suggestions");
    },
    togglePatientMode() {
      if (this.canSwitchPatient) {
        this.$emit("switch-patient-mode");
      } else {
        this.$emit("toggle-patient-mode", !this.patientModeActive);
      }
    },
    removeCapsule() {
      this.$emit("toggle-patient-mode", false);
    },
    toggleWebSearch() {
      this.$emit("toggle-web-search", !this.webSearchActive);
    },

    handleAttachClick() {
      if (this.disabled) return;
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      this.processFiles(event.target.files);
      event.target.value = "";
    },
    // Screenshots and copied images attach the same way as picked or dropped files.
    onPaste(event) {
      const files = event.clipboardData?.files;
      if (!files?.length || this.disabled || this.attachDisabled) return;
      // Word and Excel put a picture of the selection beside its text: that paste is text.
      const types = Array.from(event.clipboardData.types || []);
      if (types.includes("text/plain") || types.includes("text/html")) return;
      event.preventDefault();
      this.processFiles(files);
    },
    processFiles(fileList) {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      const remaining = Math.max(
        0,
        this.maxFiles - this.attachmentDrafts.length - this.attachmentsCreating
      );
      const refused = files.slice(remaining).map((file) => ({
        name: file.name,
        reason: `a message can carry up to ${this.maxFiles} files.`,
      }));
      const accepted = [];
      for (const file of files.slice(0, remaining)) {
        const reason = this.refusalOf(file);
        if (reason) refused.push({ name: file.name, reason });
        else accepted.push(file);
      }

      this.localNotices = refused;
      // One event for the whole set: the welcome screen creates its conversation once.
      if (accepted.length) this.$emit("files-selected", accepted);
    },
    removeAttachment(id) {
      this.$emit("remove-attachment", id);
      // The button goes with its chip: keep the keyboard in the composer.
      this.$nextTick(() => this.$refs.inputField?.focus());
    },
    dismissNotices() {
      this.localNotices = [];
      this.$emit("dismiss-notices");
    },
    onDragEnter() {
      if (this.disabled || this.attachDisabled) return;
      this.dragDepth++;
      this.isDraggingOver = true;
    },
    onDragOver(event) {
      const blocked = this.disabled || this.attachDisabled;
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = blocked ? "none" : "copy";
      }
    },
    onDragLeave() {
      if (this.disabled || this.attachDisabled) return;
      this.dragDepth = Math.max(0, this.dragDepth - 1);
      if (this.dragDepth === 0) this.isDraggingOver = false;
    },
    onDrop(event) {
      this.dragDepth = 0;
      this.isDraggingOver = false;
      if (this.disabled || this.attachDisabled) return;
      this.processFiles(event.dataTransfer && event.dataTransfer.files);
    },
    /** Why this file cannot be attached, or null when it can. */
    refusalOf(file) {
      if (file.size > this.maxFileSize) {
        return `it is over ${this.formatFileSize(this.maxFileSize)}.`;
      }
      const extension = "." + file.name.split(".").pop().toLowerCase();
      if ([".heic", ".heif"].includes(extension)) {
        return "Apple photo format can't be read. Export it as JPG or PNG.";
      }
      const extensions = this.allowedTypes
        .filter((type) => type.startsWith("."))
        .map((type) => type.toLowerCase());
      const mimeTypes = this.allowedTypes.filter(
        (type) => !type.startsWith(".")
      );
      const known =
        extensions.includes(extension) ||
        mimeTypes.some((type) =>
          type.endsWith("/*")
            ? file.type.startsWith(type.replace("/*", ""))
            : file.type === type
        );
      return known ? null : `${extension} files can't be read here.`;
    },
    formatFileSize,
    truncateFileName,
    getFileIcon,
  },
};
</script>

<style lang="scss" scoped>
.chat-input-container {
  background: var(--oliver-input-bg, rgba(255, 255, 255, 0.75));
  border: 2px solid var(--oliver-input-border, rgba(0, 0, 0, 0.15));
  border-radius: 16px;
  margin: 0 12px 12px;
  backdrop-filter: blur(8px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  flex-shrink: 0;
  min-height: 0;
  transition: border-color 0.2s ease;

  &:focus-within {
    border-color: var(--oliver-input-focus-border);
  }

  &--inline {
    margin: 0;
  }

  &--drag-over {
    border-color: var(--oliver-accent);
    border-style: dashed;
    background: var(--oliver-accent-light);
  }

  &--expanded {
    display: flex;
    flex-direction: column;
    min-height: 45vh;

    .input-area {
      flex: 1;
      min-height: 0;
      display: flex;
      flex-direction: column;
    }

    .input-row {
      flex: 1;
      min-height: 0;
      display: flex;
    }
  }
}

.input-area {
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 0;
  background: transparent;
}

.patient-capsule {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 10px 16px 0;
  padding: 4px 8px 4px 10px;
  background: var(--oliver-accent-light);
  border: 1px solid var(--oliver-accent-border);
  border-radius: 20px;
  font-size: 12px;
  color: var(--oliver-accent);
  align-self: flex-start;

  i {
    font-size: 12px;
    color: var(--oliver-accent);
    flex-shrink: 0;
  }

  span {
    color: var(--oliver-accent);
    strong {
      font-weight: 600;
      color: var(--oliver-accent);
    }
  }
}

.patient-capsule-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: none;
  background: var(--oliver-indicator-bg);
  color: var(--oliver-accent);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition: background 0.15s ease;

  i {
    font-size: 10px;
    padding-top: 1px;
    color: var(--oliver-accent);
  }

  &:hover:not(:disabled) {
    background: var(--oliver-indicator-border);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.input-row {
  padding: 10px 16px 4px;

  .chat-input-container--inline & {
    padding-top: 12px;
  }
}

.chat-input {
  width: 100%;
  border: none;
  outline: none;
  font-size: 15px;
  color: var(--oliver-text, #1a1a1a);
  background: transparent;
  font-family: inherit;
  resize: none;
  overflow-y: hidden;
  line-height: 1.5;
  min-height: 72px;
  max-height: 200px;
  box-sizing: content-box;

  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2)) transparent;

  .chat-input-container--inline & {
    min-height: 100px;
    max-height: 250px;
  }

  .chat-input-container--expanded & {
    flex: 1;
    min-height: 0;
    max-height: none;
    height: auto;
    overflow-y: auto;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2));
    border-radius: 2px;
  }

  &::placeholder {
    color: var(--oliver-text-muted, #999);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.input-bottom-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 12px 8px;
}

.input-left-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.input-right-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.input-button {
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &--icon {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    background: transparent;
    color: var(--oliver-text-muted, #666);

    i {
      font-size: 16px;
    }

    &:hover:not(:disabled) {
      background: var(--oliver-btn-subtle-hover, rgba(0, 0, 0, 0.08));
      color: var(--oliver-text, #1a1a1a);
    }
  }

  &--send {
    width: 30px;
    height: 30px;
    min-width: 30px;
    border-radius: 50%;
    background: var(--oliver-accent);
    color: white;

    i {
      font-size: 16px;
    }

    &:hover:not(:disabled) {
      opacity: 0.88;
      transform: scale(1.1);
    }

    &:disabled {
      opacity: 0.5;
    }
  }

  &--processing {
    background: var(--oliver-accent);

    &:hover:not(:disabled) {
      opacity: 0.88;
    }
  }

  &--recording {
    color: #e53e3e;
    animation: pulse 1s infinite;
  }

  &--transcribing {
    color: var(--oliver-accent);
  }
}

// Web access turned off: the clinician's choice, so it stays marked, in a neutral grey
// because the accent reads as "on".
.input-button--web-off {
  background: var(--oliver-toggle-off-bg) !important;
  border-radius: 0.75rem !important;

  i {
    position: relative;
  }

  i::after {
    content: "";
    position: absolute;
    left: 50%;
    top: 50%;
    width: 2px;
    height: 21px;
    border-radius: 1px;
    background: currentColor;
    // A gap in the chip's colour, so the slash reads apart from the globe's lines.
    box-shadow: 0 0 0 1.5px var(--oliver-toggle-off-gap);
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}

// Active state for footer-style icon buttons (prompts, patient mode)
.input-button--footer-active {
  background: var(--oliver-accent-light) !important;
  color: var(--oliver-accent) !important;
  border-radius: 0.75rem !important;

  &:hover:not(:disabled) {
    background: var(--oliver-accent-light) !important;
    border-radius: 0.75rem !important;
    opacity: 0.85;
  }
}

.input-button--switch {
  color: var(--oliver-accent) !important;

  &:hover:not(:disabled) {
    background: var(--oliver-accent-light) !important;
    color: var(--oliver-accent) !important;
  }
}

// File Previews
.file-previews {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  padding: 12px 16px;
  background: var(--oliver-bg-surface, #f8f9fa);
  border-bottom: 1px solid var(--oliver-border, rgba(0, 0, 0, 0.06));
  overflow-x: auto;
  overflow-y: hidden;

  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2)) transparent;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2));
    border-radius: 3px;

    &:hover {
      opacity: 0.8;
    }
  }
}

.drop-hint {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px;
  color: var(--oliver-accent);
  font-size: 14px;
  font-weight: 500;
}

.attach-notices {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 14px 2px;
  font-size: 12.5px;
  color: var(--oliver-text, #1a1a1a);

  &__icon {
    color: #ed8936;
    margin-top: 3px;
  }

  &__list {
    list-style: none;
    margin: 0;
    padding: 0;
    flex: 1;
    min-width: 0;

    span {
      color: var(--oliver-text-muted, #666);
    }
  }

  &__dismiss {
    border: none;
    background: none;
    font: inherit;
    font-size: 12px;
    color: var(--oliver-text-muted, #666);
    cursor: pointer;
    padding: 0;
  }
}

.attach-counter {
  font-size: 11px;
  color: var(--oliver-text-muted, #666);
  margin-right: 6px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

// Attachment button highlight when files selected
.input-button--has-files {
  background: var(--oliver-accent-light);
  color: var(--oliver-accent);

  &:hover:not(:disabled) {
    background: var(--oliver-accent-light);
    color: var(--oliver-accent);
    opacity: 0.85;
  }
}

// Animations
.file-preview-slide-enter-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.file-preview-slide-leave-active {
  transition: all 0.2s ease;
  overflow: hidden;
}

.file-preview-slide-enter-from {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.file-preview-slide-enter-to {
  max-height: 200px;
  opacity: 1;
}

.file-preview-slide-leave-from {
  max-height: 200px;
  opacity: 1;
}

.file-preview-slide-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}

.pulse-indicator {
  width: 8px;
  height: 8px;
  background: white;
  border-radius: 50%;
  animation: pulse-dot 1.5s ease-in-out infinite;
}

@keyframes pulse-dot {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.3);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.15);
  }
}

@media (max-width: 768px) {
  .file-previews {
    padding: 10px 14px;
    gap: 6px;
  }

  .input-row {
    padding: 8px 14px 12px;
    gap: 8px;
  }

  .input-button {
    &--icon,
    &--send {
      width: 32px;
      height: 32px;
      min-width: 32px;
    }
  }

  .chat-input {
    font-size: 16px;

    &::placeholder {
      font-size: 14px;
    }
  }
}
</style>
